import React from "react";
import {
  Card, CardContent, Typography, List, ListItem, ListItemAvatar, Avatar, ListItemText,
  Divider, Box, Stack, Button
} from "@mui/material";
import dayjs from 'dayjs';

export default function OrderSummary({
  id,
  currency,
  cartProducts = {},
  cartCombos = {},
  gross = 0,              // totalAmount AFTER discount (RON)
  discountValue = 0,      // NEW: discountValue (RON, gross)
  onNewOrder,
  locationName,
  scheduleOn,       // string | Date | dayjs — optional
  description       // string — optional
}) {
  const products = Object.values(cartProducts);
  const combos = Object.entries(cartCombos).map(([k, v]) => ({ key: k, ...v }));
  const money = (n) => new Intl.NumberFormat("ro-RO", { style: "currency", currency, maximumFractionDigits: 2 }).format(n);
  const fromCents = (c) => Math.round(c) / 100;
  const subtotal = gross + (discountValue || 0);

  const formatSched = (v) => dayjs(v).format("ddd, DD MMM YYYY HH:mm");
  let scheduledText = "Now";
  if (scheduleOn) {
    const parsed = dayjs(scheduleOn);
    if (parsed.isValid()) {
      scheduledText = formatSched(parsed);
    } else {
      const fallback = dayjs(String(scheduleOn).replace(" ", "T"));
      if (fallback.isValid()) scheduledText = formatSched(fallback);
    }
  }

  return (
    <Card variant="outlined">
      <CardContent id="order-summary">
        <Typography variant="h6" gutterBottom>Order #{id}</Typography>
        <Stack spacing={0.5} sx={{ mb: 1 }}>
          {locationName && (
            <Typography variant="body2" component="div">
              <b>Location:</b> {locationName}
            </Typography>
          )}
          <Typography variant="body2" component="div">
            <b>{scheduleOn ? "Scheduled at:" : "Order time:"}</b> {scheduledText}
          </Typography>
          {description && (
            <Typography variant="body2" component="div">
              <b>Description:</b> {description}
            </Typography>
          )}
        </Stack>
        <Divider sx={{ my: 1 }} />
        <List dense>
          {products.map(it => {
            const unit = fromCents(it.unitPriceC);
            const lineTotal = unit * it.qty;
            return (
              <ListItem key={`p-${it.id}`} disableGutters>
                <ListItemAvatar>
                  <Avatar variant="rounded" src={it.imageSmallUrl || undefined}>
                    {(!it.imageSmallUrl && (it.name?.[0] || "?"))}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={it.name}
                  secondary={it.qty > 1 ? `${it.qty} × ${money(unit)}` : null}
                  primaryTypographyProps={{ component: 'span' }}
                  secondaryTypographyProps={{ component: 'span' }}
                />
                <Typography variant="subtitle2">{money(lineTotal)}</Typography>
              </ListItem>
            );
          })}
          {combos.map(it => {
            const unit = fromCents(it.basePriceC);
            const lineTotal = unit * it.qty;
            const selectionNames = Object.values(it.selections || {}).map(s => s.name || s).filter(Boolean);
            return (
              <ListItem key={`c-${it.key}`} disableGutters>
                <ListItemAvatar>
                  <Avatar variant="rounded" src={it.imageSmallUrl || undefined}>🧩</Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={`${it.name} (Combo)`}
                  secondary={selectionNames.length ? selectionNames.join(", ") : null}
                  primaryTypographyProps={{ component: 'span' }}
                  secondaryTypographyProps={{ component: 'span' }}
                />
                <Typography variant="subtitle2">{money(lineTotal)}</Typography>
              </ListItem>
            );
          })}
        </List>
        <Divider sx={{ my: 1 }} />
        {/* Totals area (no recalculation from line items) */}
        {discountValue > 0 && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>Subtotal</Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>{money(subtotal)}</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
              <Typography variant="body2" sx={{ color: "error.main" }}>Discount</Typography>
              <Typography variant="body2" sx={{ color: "error.main" }}>–{money(discountValue)}</Typography>
            </Box>
          </>
        )}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Total</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{money(gross)}</Typography>
        </Box>
      </CardContent>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ p: 2, pt: 0 }}>
        <Button variant="contained" onClick={onNewOrder}>New order</Button>
      </Stack>
    </Card>
  );
}